import Vue from "vue"
import Router, {Route} from "vue-router"
import StoreDashboardPage from "@/pages/StoreDashboard.vue"
import {store} from "./store"
import logger from "@/utils/logging"
import ProductEditor from "@/components/ProductEditor.vue"
import ProductListEditor from "@/components/ProductListEditor.vue"
import EmptyState from "@/components/EmptyState.vue"
import OidcCallback from "@/components/auth/OidcCallbackPage.vue"
// @ts-ignore
import {vuexOidcCreateRouterMiddleware} from "vuex-oidc"
import StoreSelectorPage from "@/pages/StoreSelector.vue"
import AuthStart from "@/components/auth/AuthStart.vue"
import AuthPage from "@/pages/Auth.vue"
import AuthPending from "@/components/auth/AuthPending.vue"

Vue.use(Router)
const KEY_DEFAULT_STORE_ID = "defaults.storeId"
const router = new Router({
    mode: "history",
    linkActiveClass: "active",
    linkExactActiveClass: "exact-active",
    routes: [
        {
            path: "/auth",
            component: AuthPage,
            meta: {
                isPublic: true,
            },
            children: [
                {
                    path: "signin-success", // Needs to match redirect_uri in you oidcSettings
                    component: OidcCallback,
                    meta: {
                        isOidcCallback: true,
                        isPublic: true,
                    },
                },
                {
                    path: "signin-success-error", // Needs to match redirect_uri in you oidcSettings
                    component: OidcCallback,
                    meta: {
                        isOidcCallback: true,
                        isPublic: true,
                        isOidcError: true,
                    },
                },
                {
                    path: "pending",
                    component: AuthPending,
                    name: "authPending",
                    meta: {
                        isOidcCallback: true,
                    },
                },
                {
                    path: "*",
                    component: AuthStart,
                    name: "authStart",
                    meta: {
                        isPublic: true,
                        isAuthStart: true,
                    },
                },
                {path: "", redirect: "./"}, // Cannot redirect to named '*' route: https://github.com/vuejs/vue-router/issues/724
            ],
        },
        {
            path: "/stores",
            name: "storeSelector",
            component: StoreSelectorPage,
        },
        {
            path: "/stores/:storeId",
            component: StoreDashboardPage,
            beforeEnter: (to: Route, from: Route, next) => {
                localStorage.setItem(KEY_DEFAULT_STORE_ID, to.params.storeId)
                next()
            },
            children: [
                {
                    path: "products",
                    component: ProductEditor,
                    props: true,
                    children: [
                        {
                            path: "",
                            name: "emptyProductsEditor",
                            component: EmptyState,
                            props: {
                                header: "Ingen liste valgt",
                                subHeader: "Vælg venligst en liste, Så vi kan vise listens varer.",
                            },
                        },
                        {
                            path: ":productListId",
                            name: "productListEditor",
                            component: ProductListEditor,
                            props: true,
                        },
                    ],
                },
                {path: "", redirect: {name: "emptyProductsEditor"}},
                {path: "*", redirect: {name: "emptyProductsEditor"}},
            ],
        },
        {
            // will match everything
            path: "*",
            redirect: (to: Route) => {
                const defaultStoreId: string | null = localStorage.getItem(KEY_DEFAULT_STORE_ID)
                if (defaultStoreId == null) {
                    logger.info(`Matched default route without default store. Redirecting to store selector screen...`)
                    return {name: "storeSelector"}
                } else {
                    logger.info(`Matched default route. Redirecting to default store:`, defaultStoreId)
                    return {name: "emptyProductsEditor", params: {storeId: defaultStoreId}}
                }
            },
        },
    ],
})

router.beforeEach(async (to, from, next) => {
    if (to.matched.some((record) => !record.meta.isPublic)) {
        if (!from.meta.isAuthStart && !await store.actions.auth.checkHasAuthenticated()) {
            logger.debug("[Auth] User hasn't yet authenticated. Redirecting to auth page...")
            next({name: "authStart", params: {0: ""}})
        } else if (await store.actions.auth.checkAccess(to)) {
            next()
        } else {
            // If we've reached this point, it's because the route was denied, which means the OIDC framework will redirect to
            // identity server auth page momentarily
            logger.error(`Access to route denied: ${to.path}. Automatic redirection to Identity Server auth page pending...`)
            next({name: "authPending"})
        }
    } else {
        next()
    }
})

export default router
